/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * FeedSidebar
 * -----------
 * Navigation for the athlete feed: the left rail on wide screens (collapsing
 * to icons below 1240px), and on phones a top bar plus a bottom tab bar of
 * Activities, Events, Passes, Profile and a "More" sheet holding My
 * certificates, My payments and Analytics.
 *
 * Events and registrations are the portal's existing pages (/p/event and
 * /app/my-registrations). On phones and tablets they are linked through the
 * feed's own /feed/events and /feed/passes routes instead, which render those
 * same pages inside the shell so the tab bar survives the tap; useShellNav
 * decides which of the two a given width gets, and the desktop answer is the
 * portal path exactly as before.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import FeedIcon from './FeedIcon';
import AlertDialog from '../common/AlertDialog';
import useSignOut from './useSignOut';
// Notifications are hidden from the athlete feed for now; restore with the nav items.
// import { useNotifications } from '../../context/NotificationContext';
import { useAppBranding } from '../../hooks/useAppBranding';
import BrandMark from '../brand/BrandMark';
import envConfig from '../../config/envConfig';
import {
  // FEED_PATH, FEED_PROFILE_PATH, FEED_NOTIFICATIONS_PATH, FEED_ANALYTICS_PATH, EVENTS_PATH, REGISTRATIONS_PATH,
  FEED_PATH, FEED_PROFILE_PATH, FEED_ANALYTICS_PATH, EVENTS_PATH, REGISTRATIONS_PATH,
  FEED_EVENTS_PATH, FEED_PASSES_PATH, FEED_CERTIFICATES_PATH, FEED_PAYMENTS_PATH,
} from '../../pages/feed/feedRoutes';
import useShellNav from '../../pages/feed/useShellNav';

/**
 * Where "Events" and "Registrations" point at this width: inside the shell on
 * phones and tablets, the portal pages on desktop. Both are returned together
 * so the two nav bars can never disagree.
 */
function useNavTargets() {
  const embedded = useShellNav();
  return {
    events: embedded ? FEED_EVENTS_PATH : EVENTS_PATH,
    registrations: embedded ? FEED_PASSES_PATH : REGISTRATIONS_PATH,
    // An in-shell destination is a feed route, so it takes NavLink and gets
    // the active styling the other tabs have; a portal path stays a plain Link.
    embedded,
  };
}

function Brand() {
  const { data: branding, isPending } = useAppBranding();
  const pending = !branding && isPending;
  const name = branding?.app_name || envConfig.appName || 'AthloHive';
  return (
    <Link to={FEED_PATH} className="fd-brand" aria-label={pending ? 'Home' : `${name} home`} aria-busy={pending || undefined}>
      <BrandMark name={name} logo={branding?.logo} pending={pending} />
    </Link>
  );
}

const navClass = ({ isActive }) => (isActive ? 'active' : undefined);

export function FeedSidebar({ user, registrationCount }) {
  const { signOut, busy, error, clearError } = useSignOut();
  const target = useNavTargets();
  // Notifications are hidden from the athlete feed for now (see the nav below).
  // const { unreadCount = 0 } = useNotifications() || {};
  return (
    <aside className="fd-side" aria-label="Main navigation">
      <Brand />
      <nav className="fd-nav">
        <NavLink to={FEED_PATH} end className={navClass}><FeedIcon name="monitoring" /><span className="l">My activities</span></NavLink>
        <NavLink to={FEED_ANALYTICS_PATH} className={navClass}><FeedIcon name="insights" /><span className="l">Analytics</span></NavLink>
        {target.embedded
          ? <NavLink to={target.events} className={navClass}><FeedIcon name="calendar_month" /><span className="l">Events</span></NavLink>
          : <Link to={target.events}><FeedIcon name="calendar_month" /><span className="l">Events</span></Link>}
        {target.embedded ? (
          <NavLink to={target.registrations} className={navClass}>
            <FeedIcon name="confirmation_number" /><span className="l">Registrations</span>
            {registrationCount > 0 && <span className="fd-n">{registrationCount}</span>}
          </NavLink>
        ) : (
          <Link to={target.registrations}>
            <FeedIcon name="confirmation_number" /><span className="l">Registrations</span>
            {registrationCount > 0 && <span className="fd-n">{registrationCount}</span>}
          </Link>
        )}
        <NavLink to={FEED_CERTIFICATES_PATH} className={navClass}><FeedIcon name="workspace_premium" /><span className="l">My certificates</span></NavLink>
        <hr />
        {/* Hidden from the athlete feed for now.
        <NavLink to={FEED_NOTIFICATIONS_PATH} className={navClass}>
          <FeedIcon name="notifications" /><span className="l">Notifications</span>
          {unreadCount > 0 && <span className="fd-n">{unreadCount > 99 ? '99+' : unreadCount}</span>}
        </NavLink> */}
        {/* Below the divider with Profile, not above it with the activity
            items: a payment record is an ACCOUNT thing, not an athletic one. */}
        <NavLink to={FEED_PAYMENTS_PATH} className={navClass}><FeedIcon name="receipt_long" /><span className="l">My payments</span></NavLink>
        <NavLink to={FEED_PROFILE_PATH} className={navClass}><FeedIcon name="person" /><span className="l">Profile</span></NavLink>
      </nav>
      <Link to={target.events} className="fd-btn fd-btn-primary fd-cta"><FeedIcon name="calendar_month" /><span className="l">Find an event</span></Link>
      <div className="fd-me">
        <span className="fd-avatar">{user.initials}</span>
        <div style={{ minWidth: 0 }}>
          <b>{user.name}</b>
          {user.handle && <span className="h">{user.handle}</span>}
        </div>
        <button type="button" className="fd-iconbtn" title="Sign out" aria-label="Sign out" onClick={signOut} disabled={busy}>
          <FeedIcon name="logout" />
        </button>
      </div>
      <AlertDialog open={!!error} title="Logout Failed" message={error || ''} type="error" okLabel="OK" onOk={clearError} />
    </aside>
  );
}

/** Phone-only top bar: brand, notifications, profile. */
export function FeedTopBar({ user }) {
  // Notifications are hidden from the athlete feed for now.
  // const { unreadCount = 0 } = useNotifications() || {};
  return (
    <header className="fd-mtop">
      <Brand />
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {/* Hidden from the athlete feed for now.
        <Link to={FEED_NOTIFICATIONS_PATH} className="fd-iconbtn" aria-label="Notifications">
          <FeedIcon name="notifications" />
          {unreadCount > 0 && <span className="fd-dot">{unreadCount > 9 ? '9+' : unreadCount}</span>}
        </Link> */}
        <Link to={FEED_PROFILE_PATH} className="fd-avatar" aria-label="Profile">{user.initials}</Link>
      </div>
    </header>
  );
}

/*
 * What the bottom bar's "More" sheet holds. The bar has five slots and they
 * are all spoken for, so the less-visited destinations live here.
 */
export const MORE_ITEMS = [
  { to: FEED_CERTIFICATES_PATH, icon: 'workspace_premium', label: 'My certificates', sub: 'Certificates from events you finished' },
  { to: FEED_PAYMENTS_PATH, icon: 'receipt_long', label: 'My payments', sub: 'Registration payments and refunds' },
  { to: FEED_ANALYTICS_PATH, icon: 'insights', label: 'Analytics', sub: 'Your training trends' },
];

// Profile has its own tab, so it does not light up More; the rest do.
const MORE_PATHS = [FEED_CERTIFICATES_PATH, FEED_PAYMENTS_PATH, FEED_ANALYTICS_PATH];

/** The sheet the "More" tab opens: a list of links over a dimmed page. */
function MoreSheet({ open, onClose }) {
  const sheetRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // The page underneath must not scroll while the sheet is up.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    sheetRef.current?.querySelector('a')?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <>
      <div className="fd-more-scrim" onClick={onClose} aria-hidden="true" />
      <div className="fd-more-sheet" id="fd-more-sheet" role="dialog" aria-modal="true" aria-label="More" ref={sheetRef}>
        <span className="fd-more-grab" aria-hidden="true" />
        <div className="fd-more-h">
          <h2>More</h2>
          <button type="button" className="fd-iconbtn" aria-label="Close" onClick={onClose}><FeedIcon name="close" /></button>
        </div>
        <ul>
          {MORE_ITEMS.map((it) => (
            <li key={it.label}>
              <NavLink to={it.to} end className={navClass} onClick={onClose}>
                <span className="i"><FeedIcon name={it.icon} /></span>
                <span className="t"><b>{it.label}</b><small>{it.sub}</small></span>
                <FeedIcon name="chevron_right" className="ch" />
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

/** Phone-only bottom tab bar: four destinations and a "More" sheet. */
export function FeedBottomNav({ registrationCount }) {
  const target = useNavTargets();
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const closeMore = useCallback(() => setMoreOpen(false), []);
  // Any navigation — a sheet link, the back button, another tab — closes it.
  useEffect(() => { setMoreOpen(false); }, [pathname]);
  // Above the phone breakpoint the bar and sheet are hidden by CSS; close it
  // there too, or a rotated phone would keep the page's scroll locked.
  useEffect(() => {
    if (!moreOpen || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(max-width: 760px)');
    const onChange = (e) => { if (!e.matches) setMoreOpen(false); };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, [moreOpen]);
  const moreActive = MORE_PATHS.some((p) => pathname.startsWith(p));

  return (
    <>
    <MoreSheet open={moreOpen} onClose={closeMore} />
    <nav className={`fd-bnav${moreOpen ? ' fd-bnav-open' : ''}`} aria-label="Main navigation">
      <NavLink to={FEED_PATH} end className={navClass}><FeedIcon name="monitoring" />Activities</NavLink>
      {target.embedded
        ? <NavLink to={target.events} className={navClass}><FeedIcon name="calendar_month" />Events</NavLink>
        : <Link to={target.events}><FeedIcon name="calendar_month" />Events</Link>}
      {target.embedded ? (
        <NavLink to={target.registrations} className={navClass}>
          <FeedIcon name="confirmation_number" />Passes
          {registrationCount > 0 && <span className="fd-n">{registrationCount}</span>}
        </NavLink>
      ) : (
        <Link to={target.registrations}>
          <FeedIcon name="confirmation_number" />Passes
          {registrationCount > 0 && <span className="fd-n">{registrationCount}</span>}
        </Link>
      )}
      <NavLink to={FEED_PROFILE_PATH} className={navClass}><FeedIcon name="person" />Profile</NavLink>
      <button
        type="button"
        className={moreActive || moreOpen ? 'active' : undefined}
        aria-haspopup="dialog"
        aria-expanded={moreOpen}
        aria-controls={moreOpen ? 'fd-more-sheet' : undefined}
        onClick={() => setMoreOpen((v) => !v)}
      >
        <FeedIcon name="more_horiz" />More
      </button>
    </nav>
    </>
  );
}
